import type { Location } from '@up2stay/up2promo-api-client';
import { Card, CardContent } from '@up2stay/up2promo-ui';
import { Label } from '@up2stay/up2promo-ui';
import { Skeleton } from '@up2stay/up2promo-ui';
import { cn } from '@up2stay/up2promo-ui';
import { Check, MapPin } from 'lucide-react';
import { type ReactNode, useState } from 'react';

import type { ModuleEntryGovernanceProps } from '@/core/components/profile/module-entry-governance';
import {
  useEntryGovernance,
  WholeModuleGovernanceHeader,
} from '@/core/components/profile/module-entry-governance-ui';
import { useLocations } from '@/core/hooks/use-locations';
import { useScrollShadow } from '@/core/hooks/use-scroll-shadow';
import { MODULE_ENTRY_WHOLE_MODULE_KEY } from '@/core/lib/profile-module-entry-policies';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { useCurrentWorkspace } from '@/core/store/workspace.store';
import type { GoogleMapsModuleConfig } from '@/core/types/profile-modules';

interface GoogleMapsModuleConfigProps extends ModuleEntryGovernanceProps {
  config: GoogleMapsModuleConfig;
  onChange: (config: GoogleMapsModuleConfig) => void;
  fieldLockedHintMessage?: string;
}

const LOCATIONS_PAGE_LIMIT = 100;

const EMPTY_VALUE = '__none__';

function LocationCard({
  location,
  isSelected,
  onSelect,
}: {
  location: Location;
  isSelected: boolean;
  onSelect: () => void;
}) {
  return (
    <Card
      role="button"
      tabIndex={0}
      className={cn(
        'cursor-pointer border-2 transition-colors hover:border-primary/40 hover:shadow-sm',
        isSelected ? 'border-primary bg-primary/5 shadow-sm' : 'border-border',
      )}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
    >
      <CardContent className="flex items-start gap-3 p-4">
        <div
          className={cn(
            'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
            isSelected ? 'bg-primary text-primary-foreground' : 'bg-muted',
          )}
        >
          {isSelected ? (
            <Check className="h-5 w-5" />
          ) : (
            <MapPin className="h-5 w-5 text-muted-foreground" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-medium leading-tight">{location.name}</p>
          {location.address ? (
            <p className="mt-0.5 truncate text-sm text-muted-foreground">
              {location.address}
            </p>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}

function NoLocationCard({
  isSelected,
  onSelect,
  label,
}: {
  isSelected: boolean;
  onSelect: () => void;
  label: string;
}) {
  return (
    <Card
      role="button"
      tabIndex={0}
      className={cn(
        'cursor-pointer border-2 transition-colors hover:border-muted-foreground/50 hover:bg-muted/20 hover:shadow-sm',
        isSelected ? 'border-muted-foreground/40 bg-muted/30' : 'border-border',
      )}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
    >
      <CardContent className="flex items-center gap-3 p-4">
        <div
          className={cn(
            'flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 border-dashed',
            isSelected
              ? 'border-muted-foreground/60 bg-muted'
              : 'border-muted bg-muted/50',
          )}
        >
          <MapPin className="h-5 w-5 text-muted-foreground" />
        </div>
        <p className="text-sm font-medium text-muted-foreground">{label}</p>
      </CardContent>
    </Card>
  );
}

/** Tighter edge detection than useScrollShadow default (10px) for compact list rows. */
const LOCATION_LIST_SCROLL_THRESHOLD = 2;

function ScrollableLocationList({
  children,
  measureKey,
}: {
  children: ReactNode;
  /** Bumps fade recalculation when list content changes (e.g. location count). */
  measureKey: string | number;
}) {
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(
    null,
  );
  const [contentElement, setContentElement] = useState<HTMLDivElement | null>(
    null,
  );

  const { showTopShadow, showBottomShadow } = useScrollShadow({
    scrollElement,
    contentElement,
    threshold: LOCATION_LIST_SCROLL_THRESHOLD,
    measureRevision: measureKey,
  });

  return (
    <div className="relative">
      <div
        className={cn(
          'pointer-events-none absolute inset-x-0 top-0 z-10 h-8 bg-gradient-to-b from-card to-transparent transition-opacity duration-200',
          showTopShadow ? 'opacity-100' : 'opacity-0',
        )}
        aria-hidden
      />
      <div
        className={cn(
          'pointer-events-none absolute inset-x-0 bottom-0 z-10 h-8 bg-gradient-to-t from-card to-transparent transition-opacity duration-200',
          showBottomShadow ? 'opacity-100' : 'opacity-0',
        )}
        aria-hidden
      />
      <div
        ref={setScrollElement}
        className="max-h-[min(60vh,320px)] overflow-y-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
      >
        <div ref={setContentElement} className="space-y-2">
          {children}
        </div>
      </div>
    </div>
  );
}

export function GoogleMapsModuleConfigComponent({
  config,
  onChange,
  contentReadOnly = false,
  templateModule,
  showEntryPolicies = false,
  onEntryPolicyChange,
  policyLabels,
  fieldLockedHintMessage,
  workspaceId,
  templateId,
  publishStatus,

  memberRoster,
  memberRosterLoading,
  hasAssignedTeams,
  memberValuesDict,
}: GoogleMapsModuleConfigProps) {
  const { entryLocked } = useEntryGovernance({
    templateModule,
    entryId: MODULE_ENTRY_WHOLE_MODULE_KEY,
  });
  const dict = useDictionary();
  const currentWorkspace = useCurrentWorkspace();
  const [localConfig, setLocalConfig] =
    useState<GoogleMapsModuleConfig>(config);
  const [prevConfig, setPrevConfig] = useState(config);
  if (config !== prevConfig) {
    setPrevConfig(config);
    setLocalConfig(config);
  }

  const locationDict =
    (
      dict?.profiles as {
        edit?: { modules?: { location?: Record<string, string> } };
      }
    )?.edit?.modules?.location ?? {};

  const { locations, isLoading: locationsLoading } = useLocations(undefined, {
    limit: LOCATIONS_PAGE_LIMIT,
    queryKeySuffix: 'profile-module',
  });

  const handleChange = (updates: Partial<GoogleMapsModuleConfig>) => {
    const newConfig = { ...localConfig, ...updates };
    setLocalConfig(newConfig);
    onChange(newConfig);
  };

  const handleSelectNone = () => {
    handleChange({
      locationId: undefined,
      address: '',
      latitude: undefined,
      longitude: undefined,
      markerTitle: undefined,
      markerDescription: undefined,
    });
  };

  const handleSelectLocation = (location: Location) => {
    // Persist location id only — display fields are resolved live at preview/public read.
    handleChange({
      locationId: location.id,
      address: '',
      latitude: undefined,
      longitude: undefined,
      markerTitle: undefined,
      markerDescription: undefined,
    });
  };

  const selectedId = localConfig.locationId ?? EMPTY_VALUE;
  const noLocationLabel = locationDict.noLocation ?? 'Kein Standort';

  if (!currentWorkspace?.id) {
    return (
      <p className="text-sm text-muted-foreground">
        {locationDict.noLocationsHint ??
          'Bitte wähle einen Arbeitsbereich, um Standorte auszuwählen.'}
      </p>
    );
  }

  const selectionDisabled = entryLocked && !showEntryPolicies;

  return (
    <div className="space-y-3">
      <WholeModuleGovernanceHeader
        templateModule={templateModule}
        showEntryPolicy={showEntryPolicies}
        onEntryPolicyChange={onEntryPolicyChange}
        policyLabels={policyLabels}
        enableMemberValues={false}
        fieldLockedHintMessage={fieldLockedHintMessage}
        entryLabel={locationDict.selectLabel ?? 'Standort'}
        workspaceId={workspaceId}
        templateId={templateId}
        publishStatus={publishStatus}
        memberRoster={memberRoster}
        memberRosterLoading={memberRosterLoading}
        hasAssignedTeams={hasAssignedTeams}
        memberValuesDict={memberValuesDict}
        canManage={!contentReadOnly}
      />
      <Label className="text-sm font-medium">
        {locationDict.selectLabel ?? 'Standort auswählen'}
      </Label>
      <div
        className={cn(selectionDisabled && 'pointer-events-none opacity-70')}
      >
        {locationsLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-[72px] w-full rounded-lg" />
            <Skeleton className="h-[72px] w-full rounded-lg" />
            <Skeleton className="h-[72px] w-full rounded-lg" />
          </div>
        ) : (
          <ScrollableLocationList measureKey={locations.length}>
            <NoLocationCard
              isSelected={selectedId === EMPTY_VALUE}
              onSelect={handleSelectNone}
              label={noLocationLabel}
            />
            {locations.map((loc) => (
              <LocationCard
                key={loc.id}
                location={loc}
                isSelected={selectedId === loc.id}
                onSelect={() => handleSelectLocation(loc)}
              />
            ))}
          </ScrollableLocationList>
        )}
        {locations.length === 0 && !locationsLoading && (
          <p className="text-xs text-muted-foreground">
            {locationDict.noLocationsHint ??
              'Erstelle zuerst Standorte unter Arbeitsbereich → Standorte.'}
          </p>
        )}
      </div>
    </div>
  );
}
